import { useCallback, useEffect, useRef } from "react";

/** Extract the first group of exactly 6 consecutive digits from any text. */
export function extractOtpCode(text) {
  if (!text) return null;
  const match = String(text).match(/(?:^|\D)(\d{6})(?:\D|$)/);
  return match ? match[1] : null;
}

/**
 * Fill OTP from clipboard when the user returns from WhatsApp (or taps the field
 * after copying a message that contains 6 digits together). No @domain suffix needed.
 */
export function useOtpAutoFill({ enabled, onOtpReceived }) {
  const onOtpReceivedRef = useRef(onOtpReceived);
  onOtpReceivedRef.current = onOtpReceived;

  const tryFillFromClipboard = useCallback(async () => {
    if (!navigator.clipboard || !navigator.clipboard.readText) return;
    try {
      const text = await navigator.clipboard.readText();
      const code = extractOtpCode(text);
      if (code) onOtpReceivedRef.current(code);
    } catch (_err) {
      // Clipboard read requires permission; ignore when unavailable.
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const handleReturnToPage = () => {
      if (document.visibilityState === "visible") {
        tryFillFromClipboard();
      }
    };

    const handleWindowFocus = () => {
      tryFillFromClipboard();
    };

    document.addEventListener("visibilitychange", handleReturnToPage);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleReturnToPage);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [enabled, tryFillFromClipboard]);

  return { tryFillFromClipboard };
}
